import React, { useEffect, useState } from "react"
import { Modal } from "../../../NewModal"
import { Flex } from "rebass"
import { css, useTheme } from "@emotion/react"
import { FormProvider, useForm } from "react-hook-form"
import { TextInput } from "../../../forms/inputs/TextInput"
import { useAnonymousAPI } from "../../../../services/api"
import { ButtonPrimary } from "../../../button/buttons"
import { yupResolver } from "@hookform/resolvers/yup"
import { ErrorBox } from "../../../errors/ErrorBox"
import { captureException } from "../../../../services/exception-logger"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"
import { useTranslation } from "react-i18next"
import { translationKeys } from "../../../../util/translationKeys"
import { useForgotPasswordSchemaProperties } from "../../../forms/validation-schemas/use-forgot-password-scheme"
import { useAuthModals } from "../../../../context/AuthModalsContext"

const modalStyle = css`
  flex-direction: column;
  align-items: space-between;
  gap: 24px;
  width: 100%;
`

const formStyle = css`
  align-items: center;
  align-content: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: space-between;
  gap: 8px;
`

const bottomStyle = css`
  flex-direction: column;
  width: 100%;
  gap: 24px;
  padding-top: 16px;
`

type ForgotPasswordForm = {
  email: string
}

export const ForgotPasswordModal = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const api = useAnonymousAPI()

  const [error, setError] = useState<string>("")
  const [sendingReset, setSendingReset] = useState(false)

  const { visibleAuthModal, setVisibleAuthModal } = useAuthModals()

  const forgotPasswordSchemaProperties = useForgotPasswordSchemaProperties()

  const methods = useForm<ForgotPasswordForm>({
    mode: "onChange",
    resolver: yupResolver(forgotPasswordSchemaProperties),
  })

  const { handleSubmit, reset } = methods

  useEffect(() => {
    if (
      visibleAuthModal === "login" ||
      visibleAuthModal === "forgot-password"
    ) {
      return
    }
    reset()
  }, [visibleAuthModal])

  const goBackStyle = css`
    position: absolute;
    left: 24px;
    top: 20px;
    gap: 8px;
    padding-top: 2px;
    padding-bottom: 2px;
    padding-left: 8px;
    cursor: pointer;
  `

  const onSubmit = async (data: ForgotPasswordForm) => {
    setSendingReset(true)
    try {
      await api.auth.resetPassword({ email: data.email })
      if (window.analytics) {
        window.analytics.track("Reset password requested")
      }
      setVisibleAuthModal("login")
      reset()
      setSendingReset(false)
    } catch (error: any) {
      const errorMessage = "Something went wrong. Could not send email."
      setError(errorMessage)
      captureException(error)
      setSendingReset(false)
    }
  }

  return (
    <Modal
      title={`${t(translationKeys.comboResetPasswordTitle)}`}
      subtitle={`${t(translationKeys.webForgotpasswordSubtitle)}`}
      isVisible={visibleAuthModal === "forgot-password"}
      onClose={() => setVisibleAuthModal(undefined)}
    >
      <Flex css={modalStyle}>
        <Flex css={goBackStyle} onClick={() => setVisibleAuthModal("login")}>
          <FontAwesomeIcon icon={solid("chevron-left")} />
        </Flex>
        <FormProvider {...methods}>
          <form>
            <Flex css={formStyle}>
              <TextInput
                label={`${t(translationKeys.comboCreateaccountEmail)}`}
                placeholder={`${t(translationKeys.comboSignupEmailHint)}`}
                name="email"
                required={true}
              />
              <Flex css={bottomStyle}>
                {error && <ErrorBox title={error} />}
                <ButtonPrimary
                  disabled={false}
                  type="button"
                  backgroundColor={theme.colors.ctaMain}
                  hoverColor={theme.colors.ctaSecondaryOne}
                  color={theme.colors.white}
                  loading={sendingReset}
                  onClick={handleSubmit(onSubmit)}
                >
                  {t(translationKeys.webForgotpasswordButtoncta)}
                </ButtonPrimary>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      </Flex>
    </Modal>
  )
}
